import { useState } from "react";
import { Vote, Check, X, Loader2, Gavel, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const CHOICE_LABEL = { for: "For", mot: "Mot", avstar: "Avstår" };

function ChoiceBtns({ current, onPick, disabled, testidPrefix }) {
  return (
    <div className="flex gap-1.5">
      {["for", "mot", "avstar"].map((c) => (
        <button
          key={c}
          type="button"
          data-testid={`${testidPrefix}-${c}`}
          disabled={disabled}
          onClick={() => onPick(c)}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
            current === c
              ? c === "for"
                ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : c === "mot"
                ? "border-red-500 bg-red-500/15 text-red-700 dark:text-red-300"
                : "border-foreground/40 bg-muted text-foreground"
              : "border-border hover:bg-muted"
          }`}
        >
          {CHOICE_LABEL[c]}
        </button>
      ))}
    </div>
  );
}

export function LivePoll({ meetingId, item, live, refreshLive, load }) {
  const [busy, setBusy] = useState(false);
  const isChair = !!live?.is_chair;
  const pv = live?.polls?.[item.id] || null;
  const status = pv?.status || null;

  const call = async (path, body, after) => {
    setBusy(true);
    try {
      await api.post(`/meetings/${meetingId}/agenda/${item.id}/poll/${path}`, body || {});
      refreshLive?.();
      after?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3" data-testid={`poll-none-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Vote className="h-4 w-4" /> Sanntidsavstemning
          </span>
          {isChair ? (
            <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => call("open")} data-testid={`poll-open-btn-${item.id}`}>
              <Gavel className="h-4 w-4" /> Åpne avstemming
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">Ikke åpnet ennå.</span>
          )}
        </div>
      </div>
    );
  }

  const tally = pv.tally || { for: 0, mot: 0, avstar: 0 };

  if (status === "closed") {
    const vedtatt = pv.result?.outcome === "vedtatt";
    return (
      <div className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3" data-testid={`poll-result-${item.id}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${vedtatt ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-red-500/15 text-red-700 dark:text-red-300"}`}>
            {vedtatt ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />} {vedtatt ? "Vedtatt" : "Forkastet"}
          </span>
          <span className="text-xs text-muted-foreground">For {tally.for} · Mot {tally.mot} · Avstår {tally.avstar}</span>
          {isChair ? (
            <Button size="sm" variant="ghost" className="ml-auto gap-1.5 text-xs" disabled={busy} onClick={() => call("reset", null, load)} data-testid={`poll-reset-${item.id}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Ny avstemming
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const total = tally.for + tally.mot + tally.avstar;
  const pct = (n) => (total ? Math.round((100 * n) / total) : 0);

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-primary/25 bg-primary/[0.04] p-3" data-testid={`poll-open-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-lime px-2.5 py-0.5 text-xs font-semibold text-lime-foreground">
          <Vote className="h-3.5 w-3.5" /> Avstemming åpen
        </span>
        <span className="text-xs text-muted-foreground" data-testid={`poll-progress-${item.id}`}>{pv.voted} av {pv.eligible} har stemt</span>
        <span className="ml-auto text-xs text-muted-foreground">For {tally.for} · Mot {tally.mot} · Avstår {tally.avstar}</span>
      </div>

      <div className="space-y-1.5" data-testid={`poll-bars-${item.id}`}>
        {["for", "mot", "avstar"].map((c) => (
          <div key={c} className="flex items-center gap-2">
            <span className="w-14 text-xs font-medium">{CHOICE_LABEL[c]}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full transition-all duration-500 ${c === "for" ? "bg-emerald-500" : c === "mot" ? "bg-red-500" : "bg-foreground/40"}`}
                style={{ width: `${pct(tally[c])}%` }}
              />
            </div>
            <span className="w-8 text-right text-xs text-muted-foreground">{tally[c]}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 bg-card p-2.5">
        <span className="text-xs font-medium">Din stemme</span>
        <ChoiceBtns current={pv.my_choice} disabled={busy} onPick={(c) => call("vote", { choice: c })} testidPrefix={`poll-myvote-${item.id}`} />
      </div>

      {isChair ? (
        <div className="flex justify-end">
          <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => call("close", null, load)} data-testid={`poll-close-${item.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />} Avslutt &amp; fastsett vedtak
          </Button>
        </div>
      ) : null}
    </div>
  );
}
